<template>
  <div v-if="shown" class="mt-3" data-cy="crew-labor-rate">
    <template v-if="canSetRates">
      <div class="flex flex-col gap-3 md:flex-row">
        <TextField
          v-model="rateCents"
          currency
          disable-negative
          label="Hourly labor cost"
          name="new_hourly_labor_cost_cents"
        />
        <Datepicker
          :model-value="effectiveOn"
          format="MM/DD/YYYY"
          label="Effective date"
          :input-attr="{ 'data-cy': 'labor-rate-effective-on' }"
          @update:model-value="onEffectiveOn"
          @clear-date="clearEffectiveOn"
        />
      </div>
      <p class="text-xs text-gray-500 mt-1" data-cy="labor-rate-hint">
        Fully loaded cost per hour (wage + burden).
        <template v-if="showDepartmentHint">
          Leave blank to use the department rate.
        </template>
        A rate applies to work done <b>on or after</b> its effective date;
        earlier jobs keep the rate they were costed at.
      </p>
    </template>
    <p
      v-if="loading"
      class="text-sm text-gray-600 mt-3"
      data-cy="labor-rate-loading"
    >
      Loading rate history...
    </p>
    <p v-else-if="loadFailed" class="text-sm text-red-600 mt-3">
      <span data-cy="labor-rate-error">Couldn't load the rate history.</span>
      <button
        type="button"
        class="underline ml-2"
        data-cy="labor-rate-retry"
        @click="load"
      >
        Retry
      </button>
    </p>
    <div
      v-else-if="hasHistory"
      class="border border-gray-300 rounded-lg overflow-hidden mt-3"
    >
      <div
        class="text-xs text-gray-500 font-semibold bg-gray-100 px-3 py-2 uppercase leading-4"
      >
        Rate history
      </div>
      <!-- Scrolls past 5 rows (header pinned); a sliver of the 6th shows there is more. -->
      <div class="max-h-56 overflow-y-auto" data-cy="labor-rate-history-scroll">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-white">
            <tr class="text-xs text-gray-500">
              <th scope="col" class="text-left font-medium px-3 py-2">
                Effective date
              </th>
              <th scope="col" class="text-right font-medium px-3 py-2">
                Rate / hour
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in payload.labor_rate_history"
              :key="row.effective_on"
              class="border-t border-gray-200"
              data-cy="labor-rate-history-row"
            >
              <td class="px-3 py-2">
                <div class="flex flex-wrap items-center gap-1">
                  <span>{{ formatEffectiveOn(row) }}</span>
                  <Badge
                    v-if="isInEffect(row)"
                    variant="chip"
                    color="green"
                    text="In effect"
                    data-cy="labor-rate-in-effect"
                  />
                </div>
              </td>
              <td class="px-3 py-2 text-right tabular-nums">
                {{ dollars(row.rate_cents) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import type { CurrentAdminData } from "@/components/AppHeader/types";
import Badge from "@/components/Badge.vue";
import Datepicker from "@/components/Datepicker.vue";
import TextField from "@/components/TextField.vue";
import { dollars } from "@/mixins";

interface ApiLink {
  href: string;
  method: string;
}

interface LaborRateHistoryEntry {
  rate_cents: number;
  effective_on: string;
}

interface CrewMemberLaborRatePayload {
  hourly_labor_cost_cents?: number | null;
  hourly_labor_cost_effective_on?: string | null;
  labor_rate_history?: LaborRateHistoryEntry[];
}

const props = withDefaults(
  defineProps<{
    crewMember?: { links?: { crew_member_show?: ApiLink } } | null;
  }>(),
  { crewMember: null }
);

const rateCents = defineModel<number | "">("rateCents", { default: "" });
// Null, never "": the Datepicker renders an empty string as "Invalid date".
const effectiveOn = defineModel<string | null>("effectiveOn", {
  default: null
});

const store = useStore();
const loading = ref(false);
const loadFailed = ref(false);
const payload = ref<CrewMemberLaborRatePayload | null>(null);

const showLink = computed(() => props.crewMember?.links?.crew_member_show);

const jobCostingEnabled = useFeatureFlag("job_costing_ff_enabled");
// The SPA only knows whether the admin can view or set rates, never the role: under
// rbac `role` holds a display name.
const currentAdmin = computed(
  () => store.state.currentAdmin as CurrentAdminData | null
);
const canViewRates = computed(
  () => currentAdmin.value?.can_view_labor_rates === true
);
const canSetRates = computed(
  () => currentAdmin.value?.can_set_labor_rates === true
);
const visible = computed(
  () => jobCostingEnabled.value === true && canViewRates.value
);
const hasHistory = computed(
  () => (payload.value?.labor_rate_history?.length ?? 0) > 0
);
// A viewer who cannot set gets the history only, as the ERB technician forms do.
const shown = computed(
  () =>
    visible.value &&
    (canSetRates.value || loading.value || loadFailed.value || hasHistory.value)
);

// "No personal rate" means none in effect today, so a future-dated rate still
// falls back to the department. A create has nothing to load, so it is blank.
const showDepartmentHint = computed(() => {
  if (props.crewMember === null) return true;
  if (loading.value || loadFailed.value) return false;
  if (!payload.value || !Array.isArray(payload.value.labor_rate_history)) {
    return false;
  }
  return payload.value.hourly_labor_cost_cents === null;
});

async function load() {
  if (!showLink.value) return;
  loading.value = true;
  loadFailed.value = false;
  try {
    payload.value = await store.dispatch("fetchCrewMemberLaborRate", {
      link: showLink.value
    });
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

// The Datepicker emits a full ISO timestamp; the API wants a calendar day.
function onEffectiveOn(iso: string | null) {
  effectiveOn.value = iso ? moment(iso).format("YYYY-MM-DD") : null;
}

function clearEffectiveOn() {
  effectiveOn.value = null;
}

function formatEffectiveOn(row: LaborRateHistoryEntry): string {
  return moment(row.effective_on, "YYYY-MM-DD").format("MMM D, YYYY");
}

// The server's own notion of today, never the browser clock.
function isInEffect(row: LaborRateHistoryEntry): boolean {
  return row.effective_on === payload.value?.hourly_labor_cost_effective_on;
}

// The fetch follows `visible`, not `shown`: a viewer who cannot set has to load
// before the block knows whether there is history to show, so a block that ends
// up rendering nothing may still have fetched. A flag that resolves after mount
// still loads once.
watch(
  visible,
  (open) => {
    if (open && showLink.value && !payload.value && !loading.value) load();
  },
  { immediate: true }
);
</script>
